import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CommonService } from 'src/app/services/common.service';
import { PushToDeviceService } from 'src/app/services/push-to-device.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SiteSequencerService } from 'src/app/components/site-sequencer/services/site-sequencer.service';
import { OTA_Status } from '../../enums/SequenceKeys.enum';
import { DeviceHierarchy } from '../../interfaces/interfaces';
import { DeliveryDebugInfoModalComponent } from '../delivery-debug-info-modal/delivery-debug-info-modal.component';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EquipmentType } from '../../enums/equipment-builder.enum';
import { EquipmentBuilderService } from '../../services/equipment-builder.service';
import { CddPdfService } from '../../services/equipment-builder-cdd/cdd-pdf.service';

@Component({
  selector: 'app-push-device-popup',
  templateUrl: './push-device-popup.component.html',
  styleUrls: ['./push-device-popup.component.scss'],
  standalone: false
})
export class PushDevicePopupComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('searchTextField') searchField!: ElementRef;
  originalData: any[] = [];
  searchText: string = '';
  dropdownAccordion: { [key: string]: boolean } = {};
  CCUS: any[] = [];
  filteredCCUs: any[] = []; 
  siteRef!: string;
  sequenceId!: string;
  sequenceName!: string;
  isHierarchyLoading: boolean = false;
  isApiInProgress: boolean = false;
  selectedDevicesMap: any = {};
  initialSelectedDevicesMap: any = {};
  tooltip: boolean = false;
  customTooltipText: string = '';
  allSequencesOfSite: any[] = [];
  originalAppliedList: string[] = [];
  createdPointsCollection: string[] = [];
  fromEquipmentBuilder: boolean = false;
  equipBuilderData: { snippet: string; type: string; metadata: any[] } | null = null;
  allModels: any[] = [];
  modelIds: {id:string, name:string}[] = [];
  invalidModels: {id:string, name:string}[] = [];
  deviceType: string = 'CONNECT_MODULE';
  private destroy$ = new Subject<void>();
  deviceRefMap: Record<string, any> = {};
  dialogData: any;

  constructor(private pushToDeviceService: PushToDeviceService, private commonService: CommonService,
    private sitesequencerService: SiteSequencerService,private dialog: MatDialog,
    private equipmentBuilderService: EquipmentBuilderService,
    private cddPdfService: CddPdfService,
    public dialogRef: MatDialogRef<PushDevicePopupComponent>,
    @Inject(MAT_DIALOG_DATA) data: any
  ) { 
    this.dialogData = data;
  }

  async ngOnInit(): Promise<void> {
    this.isHierarchyLoading = true;
    try {
      const res:any = await lastValueFrom(
        this.sitesequencerService.getModalOptions().pipe(takeUntil(this.destroy$))
      );
      this.allModels = res || [];
      this.invalidModels = this.modelIds.filter((model: any) => !this.allModels.find((m: any) => m.id === model.id));
    } catch(err:any) {
      this.commonService.showToast('error', err.error.error || 'Error fetching models');
      this.allModels = [];
      this.invalidModels = [];
    }
   
    this.getAllSequences().then(() => {
      this.getDeviceHierarchy();
    });
    
  }

  getAllSequences(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.sitesequencerService.getSequencesBySite(this.siteRef)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          if (data && data.length > 0) {
            this.allSequencesOfSite = data;
          }
          resolve();
        }, (err) => {
          this.commonService.showToast('error', 'Error fetching sequences');
          resolve(); // Still resolve so UI isn't blocked
        });
    });
  }

  getDeviceHierarchy() {
    this.isHierarchyLoading = true;
    this.pushToDeviceService.getDeviceHierarchy(this.siteRef, this.sequenceId)
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: any) => {
      if(data && data.ccuList && Array.isArray(data.ccuList) && data.ccuList.length > 0) {
        data.ccuList.forEach((ccu: any) => {
          this.dropdownAccordion[ccu.ccuName] = true;
        })
        data.ccuList = this.sortAndFormat(data.ccuList);
        this.originalData = data.ccuList;
        this.CCUS = data.ccuList;
        this.filteredCCUs = this.CCUS;
        this.deviceRefMap = {};
        this.CCUS.forEach((ccu: any) => {
          this.getHierarchyGroups(ccu).forEach((group: any) => {
            group.devices?.forEach((device: any) => {
              this.deviceRefMap[device.deviceRef] = device;
            });
          });
        });
        
        this.selectedDevicesMap = { ...(this.initialSelectedDevicesMap || {}) }; // Restore equipment-builder selections when coming back.
        this.preselectAppliedDevices(); // Preselect devices that are already applied based on appliedSeqId key in the response. 

      } else {
        this.originalData = [];
        this.CCUS = [];
        this.filteredCCUs = [];
      }
      this.isHierarchyLoading = false;
    }, (err) => {
      this.isHierarchyLoading = false;
      this.commonService.showToast('error', 'Error fetching device hierarchy');

      this.originalData = [];
      this.CCUS = [];
      this.filteredCCUs = []; 
    });
  }
  sortAndFormat(data:DeviceHierarchy[]) {
    return data
      .slice() // Clone to avoid mutating the original array
      .sort((a, b) => a.ccuName.localeCompare(b.ccuName))
      .map(ccu => ({
        ...ccu,
        systemManagerEquips: (ccu as any).systemManagerEquips
          ?.slice()
          .sort((a: any, b: any) => a.systemManagerEquipName.localeCompare(b.systemManagerEquipName))
          .map((systemManagerEquip: any) => this.formatDeviceGroup(systemManagerEquip)) || [],
        rooms: (ccu.rooms || [])
          .slice()
          .sort((a, b) => a.roomName.localeCompare(b.roomName))
          .map(zone => this.formatDeviceGroup(zone))
      }));
  }

  formatDeviceGroup(group: any) {
    const devices = group.devices || [];
    const hasPcnDevice = devices.some((device: any) => device?.deviceName?.toLowerCase()?.includes('pcndevice'));

    const formatDeviceName = (device: any): string => {
      const originalName = device.deviceName;
      const lowerName = originalName.toLowerCase();

      if (lowerName.includes('connectnodedevice')) {
        return hasPcnDevice ? `Connect Module (${device.address})` : `Connect Node (${device.address})`;
      }

      if (lowerName.includes('pcndevice')) {
        return `Smart Node - Custom Code (${device.address})`;
      }

      if (lowerName.includes('customcontrollersmartnodedevice')) {
        return `Smart Node - Custom Controller (${device.address})`;
      }

      return originalName;
    };

    return {
      ...group,
      devices: devices
        .slice()
        .map((device: any) => ({
          ...device,
          deviceName: formatDeviceName(device),
          ...(device.otaStatus !== undefined && {
            otaStatus: this.commonService.getDeviceOtaStatus(device.otaStatus, device.deviceName)
          })
        }))
        .sort((a: any, b: any) => a.deviceName.localeCompare(b.deviceName))
    };
  }

  getHierarchyGroups(ccu: any): any[] {
    
    // Equipment Builder: Determine which groups to return based on the equipment type if coming from the equipment builder.
    if (this.fromEquipmentBuilder) {
      const equipmentType = (this.dialogData?.equipmentType || '').toLowerCase();
      if (equipmentType === EquipmentType.SYSTEM) {
        return [...(ccu?.systemManagerEquips || [])];
      }
      if (equipmentType === EquipmentType.ZONE) {
        return [...(ccu?.rooms || [])];
      }
    }

    return [
      ...(ccu?.systemManagerEquips || []),
      ...(ccu?.rooms || [])
    ];
  }

  getGroupName(group: any, ccu?: any): string {
    if (group?.systemManagerEquipRef || group?.systemManagerEquipName) {
      const ccuName = ccu?.ccuName || '';
      return ccuName ? `${ccuName} - System Manager` : 'System Manager';
    }
    return group?.roomName || '';
  }

  getGroupPayloadRef(group: any): any {
    if (group?.systemManagerEquipRef) {
      return { systemManagerRef: group.systemManagerEquipRef };
    }
    return { roomRef: group?.roomRef };
  }

  preselectAppliedDevices() {
    this.CCUS.forEach((ccu: any) => {
      this.getHierarchyGroups(ccu).forEach((group: any) => {
        group.devices?.forEach((device: any) => {
          if (device.appliedSeqId === this.sequenceId) {
            this.originalAppliedList.push(device.deviceRef);
            this.selectedDevicesMap[device.deviceRef] = true;
          }

          // Add appliedSeqName for tooltip
          if (device.appliedSeqId) {
            const targetSequence = this.allSequencesOfSite.find(seq => seq.seqId === device.appliedSeqId);
            device.appliedSeqName = targetSequence?.seqName || '';
          }
        });
        // Adding otaStatus to the room based on the devices otaStatus
        // If all devices have the same otaStatus, set the status to the room, otherwise set it to OTA_IN_PROGRESS
        const deviceOtaStatusArray = group?.devices?.filter((device: any) => device?.appliedSeqId != null && device?.appliedSeqId == this.sequenceId) || [];
        const isSameStatusForAllDevices = deviceOtaStatusArray.every((status: any) => status?.otaStatus === deviceOtaStatusArray[0]?.otaStatus);
        isSameStatusForAllDevices ? group['otaStatus'] = deviceOtaStatusArray[0]?.otaStatus : group['otaStatus'] = OTA_Status.OTA_IN_PROGRESS;
      });
    });
  }

  /**
   * Method to filter CCUs and their rooms and devices based on the search text.
   */
  filterCCUs() {
    const searchedText = this.searchText.toLowerCase();

    // Early return if search text is empty (avoid filtering)
    if (!this.searchText) {
      this.filteredCCUs = this.CCUS;
      return;
    }
    
    this.filteredCCUs = [];
    
    for (const ccu of this.CCUS) {
      const isCcuMatched = ccu.ccuName.toLowerCase().includes(searchedText);
      const filteredSystemManagerEquips = this.filterGroups(ccu.systemManagerEquips, searchedText, isCcuMatched, ccu);
      const filteredRooms = this.filterGroups(ccu.rooms, searchedText, isCcuMatched, ccu);
      const hasFilteredGroups = filteredSystemManagerEquips.length > 0 || filteredRooms.length > 0;

      if (isCcuMatched || hasFilteredGroups) {
        this.filteredCCUs.push({
          ...ccu,
          systemManagerEquips: filteredSystemManagerEquips,
          rooms: filteredRooms
        });
      }

      this.dropdownAccordion[ccu.ccuName] = true;
    }
  }

  filterGroups(groups: any[] = [], searchedText: string, isCcuMatched: boolean, ccu?: any): any[] {
    if (isCcuMatched) {
      return groups;
    }

    return groups.map((group: any) => {
        const isGroupMatched = this.getGroupName(group, ccu).toLowerCase().includes(searchedText);
        const filteredDevices = isGroupMatched
          ? group.devices
          : (group.devices?.filter((device: any) =>
              device.deviceName.toLowerCase().includes(searchedText) ||
              device.address?.toString().toLowerCase().includes(searchedText)
            ) || []);

        const includeGroup = isGroupMatched || filteredDevices.length > 0;

        if (includeGroup) {

          return {
            ...group,
            devices: filteredDevices
          };
        }

        return null;
      }).filter(Boolean);
  }

  // Update search and filter the CCUs
  onSearchChange() {
    this.filterCCUs();
  }

  areAllGroupsSelected(ccu: any): boolean {
    const groups = this.getHierarchyGroups(ccu);
    if (!groups.length) return false;

    return groups.every((group: any) => {
      const enabledDevices = group.devices?.filter((device: any) => !this.isDeviceDisabled(device)) || [];

      if (enabledDevices.length === 0) return false;

      return enabledDevices.every(
        (device: any) => this.selectedDevicesMap[device.deviceRef]
      );
    });
  }

  areSomeGroupsSelected(ccu: any): boolean {
    return this.getHierarchyGroups(ccu).some((group: any) => {
      const enabledDevices = group.devices?.filter((device: any) => !this.isDeviceDisabled(device)) || [];

      return enabledDevices.some(
        (device: any) => this.selectedDevicesMap[device.deviceRef]
      );
    });
  }

  areAllRoomsSelected(ccu: any): boolean {
    return this.areAllGroupsSelected(ccu);
  }

  areSomeRoomsSelected(ccu: any): boolean {
    return this.areSomeGroupsSelected(ccu);
  }

  toggleAllGroups(ccu: any, checked: boolean): void {
    this.getHierarchyGroups(ccu).forEach((group: any) => {
      if (!this.isGroupDisabled(group)) {
        group.devices?.forEach((device: any) => {
          if (!this.isDeviceDisabled(device)) {
            this.selectedDevicesMap[device.deviceRef] = checked;
          }
        });
      }
    });
  }

  toggleAllRooms(ccu: any, checked: boolean): void {
    this.toggleAllGroups(ccu, checked);
  }

  onRoomChange(room: any, event: MatCheckboxChange): void {
    room.enabled = event.checked;
  }

  areAllDevicesSelected(group: any): boolean {
    const enabledDevices = group.devices?.filter((device: any) => !this.isDeviceDisabled(device)) || [];
    if (enabledDevices.length === 0) return false;
    return enabledDevices.every((device: any) => this.selectedDevicesMap[device.deviceRef]);
  }

  areSomeDevicesSelected(group: any): boolean {
     const enabledDevices = group.devices?.filter((device: any) => !this.isDeviceDisabled(device)) || [];
     return enabledDevices.some((device: any) => this.selectedDevicesMap[device.deviceRef]);
  }

  toggleAllDevices(group: any, checked: boolean): void {
    group.devices?.forEach((device: any) => {
      if (!this.isDeviceDisabled(device)) {  // Skip disabled devices
        this.selectedDevicesMap[device.deviceRef] = checked;
      }
    });
  }

  onDeviceChange(deviceRef: any, event: MatCheckboxChange): void {
    this.selectedDevicesMap[deviceRef] = event.checked;
  }
  
  ngAfterViewInit(): void {
    this.searchField.nativeElement.blur();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  dropdownListAccordion(ccuName: string) {
    this.dropdownAccordion[ccuName] = !this.dropdownAccordion[ccuName];
  }

  clearSearch() {
    this.searchText = '';
    this.filterCCUs(); // Reapply filter after clearing search
  }

  isAnyDeviceSelected(): boolean {
    return Object.values(this.selectedDevicesMap).some(val => val);
  }


  isDeviceDisabled(device: any): boolean {
    const isDisabledForAppliedSeq = device.appliedSeqId && device.appliedSeqId !== this.sequenceId;
    const isDisabledForInvalidModels = (this.createdPointsCollection.length == 0 || this.invalidModels.length > 0) && !this.selectedDevicesMap[device.deviceRef];
    const isDisabledForDeviceType = this.isDeviceTypeMismatch(device) && !this.selectedDevicesMap[device.deviceRef];
    return isDisabledForAppliedSeq || isDisabledForInvalidModels || isDisabledForDeviceType;
  }

  isGroupDisabled(group: any): boolean {
    return group?.devices?.every((device: any) => this.isDeviceDisabled(device)) || false;
  }

  isRoomDisabled(room: any): boolean {
    return this.isGroupDisabled(room);
  }

  isCCUDisabled(ccu: any): boolean {
    if (!ccu) return false;
    const groups = this.getHierarchyGroups(ccu);
    return groups.length === 0 || groups.every((group: any) => this.isGroupDisabled(group));
  }

  /**
   * Method to check if any device in the hierarchy has an OTA status.
   * This is used to toggle the disabled state of Delivery logs button.
   * @returns {boolean} - true if any device has an OTA status, false otherwise.
   */
  isAnyDeviceHavingOtaStatus(): boolean {
    return this.CCUS.some((ccu: any) =>
      this.getHierarchyGroups(ccu).some((group: any) =>
        group.devices?.some((device: any) => device.otaStatus)
      )
    );
  }

  applySequence() {
    this.isApiInProgress = true;
    const payload: any[] = [];
    this.CCUS.forEach((ccu: any) => {
      this.getHierarchyGroups(ccu).forEach((group: any) => {
        group.devices.forEach((device: any) => {
          if (this.selectedDevicesMap[device.deviceRef]) {
            payload.push({
              ccuRef: ccu.ccuRef,
              ...this.getGroupPayloadRef(group),
              entityRef: device.deviceRef
            });
          }
        });
      });
    });

    // Apply the sequence either from the equipment builder
    if (this.fromEquipmentBuilder) {
      const applyRequest = this.buildEquipmentBuilderApplyRequest(payload);
      const regionId = this.dialogData?.siteData?.region?.regionId || undefined;
      const cddFile = this.cddPdfService.getGeneratedCddFile()?.file || null;

      this.equipmentBuilderService.applySequence(applyRequest, cddFile, regionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          this.dialogRef.close({ success: true, data });
          this.commonService.showToast('success', `${applyRequest.sequenceName} sequence has been applied successfully`);
          this.cddPdfService.clearGeneratedCddFile();
          this.isApiInProgress = false;
        }, (err) => {
          this.isApiInProgress = false;
          this.commonService.showToast('error', err?.error?.error || err?.message || 'Error applying sequence');
        });
    } else {
      // Apply the sequence for site sequencer Scenario
      this.pushToDeviceService.applySequence(this.sequenceId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          this.dialogRef.close(true);
          if (data && data.appliedData) {
            this.commonService.showToast('success', 'Sequence applied successfully');
          } else {
            this.commonService.showToast('error', 'Error applying sequence');
          }
          this.isApiInProgress = false;
        }, (err) => {
          this.isApiInProgress = false;
          this.commonService.showToast('error', 'Error applying sequence');
        });
    }
  } 

  private buildEquipmentBuilderApplyRequest(applyEntityList: any[]): any {
    const sequenceData = this.dialogData?.sequenceData || {};
    const sequencerBuildData = this.dialogData?.sequencerBuildData || {};
    const equipmentType = (this.dialogData?.equipmentType || this.equipBuilderData?.type || '').toString().toUpperCase();
    const sequenceName = this.sequenceName || sequencerBuildData.sequenceName || sequenceData.seqName || sequenceData.sequenceName || '';

    return {
      equipmentType,
      siteRef: this.siteRef,
      workspaceName: sequenceData.workspaceName || sequenceData.seqName || sequenceData.sequenceName || sequenceName,
      workspaceXml: sequencerBuildData.workspaceXml || sequenceData.workspaceXml || '',
      sequenceName,
      blocklyXml: sequenceData.blocklyXml || '',
      snippet: sequencerBuildData.snippet || this.equipBuilderData?.snippet || sequenceData.snippet || '',
      metadata: this.equipBuilderData?.metadata || sequenceData.metadata || [],
      applyEntityList,
      equipmentDetails: {}
    };
  }

  getDeliveryLogs() {
    this.dialogRef.addPanelClass('visibility-hidden'); // hide;
    const dialogRef = this.dialog.open(DeliveryDebugInfoModalComponent, {
      panelClass: ['fs-mat-dialog-container', 'full-height', 'sequence-logs-modal', 'ccu-logs-modal'],
      width: '800px',
      // Keep Escape scoped to the active nested dialog. Open dropdowns handle
      // the key first, and MatDialog closes only this topmost modal.
      disableClose: false,
      autoFocus: false
    });
    dialogRef.componentInstance.sequenceId = this.sequenceId;
    dialogRef.componentInstance.sequenceName = this.sequenceName;
    dialogRef.componentInstance.siteId = this.siteRef;
    dialogRef.componentInstance.isDeliveryLogsPopup = true;
    dialogRef.updatePosition({ top: '60px' });

    dialogRef.afterClosed().subscribe(result => {
      this.dialogRef.removePanelClass('visibility-hidden'); // show
    });
  }

  getStatusIconPath(status: string) {
    return this.commonService.getStatusIconPath(status);
  }

  // Method to handle the tooltip display and update its position in align with dropdown menu
  hoveredFunction(event: Event, showTooltip: boolean, data: any) {
    if (showTooltip) {
      this.tooltip = true;
       if(!this.createdPointsCollection.length || this.invalidModels.length) {
        this.customTooltipText = `This ${data.deviceName} is disabled because the sequence does not have valid models.`;
      } else if (this.isDeviceTypeMismatch(data)) {
        const deviceTypeLabel = this.isSmartNodeDevice(data) ? 'Smart Node' : (this.isConnectModuleDevice(data) ? 'Connect Module' : 'this device type');
        this.customTooltipText = `This ${data.deviceName} is disabled because the sequence is built for <strong>${this.getDeviceTypeLabel()}</strong>, which <strong>${deviceTypeLabel}</strong> does not support.`;
      } else {
        this.customTooltipText = `This ${data.deviceName} is disabled because the sequence from the  <strong>${data.appliedSeqName}</strong> Canvas is already applied. Only one sequence can be active per module at a time.`;
      }
      
      const tooltipElement = document.getElementById('custom-hierarchy-tooltip');
      const hoveredElement = event?.target as HTMLElement;

      if (tooltipElement && hoveredElement) {
        const hoveredRect = hoveredElement.getBoundingClientRect();
        const tooltipHeight = tooltipElement.offsetHeight;
        const tooltipWidth = tooltipElement.offsetWidth;

        // Dynamically get modal's left position
        const modalElement = document.querySelector('.cdk-overlay-pane.fs-push-to-device-dialog');
        let left = 0;

        if (modalElement) {
          const modalRect = modalElement.getBoundingClientRect();
          left = modalRect.left - tooltipWidth - 6; // 12px gap between modal and tooltip
        } else {
          left = hoveredRect.left - tooltipWidth - 6; // fallback if modal not found
        }

        // Vertical alignment with hovered element
        let top = hoveredRect.top;

        // Prevent bottom overflow
        if (top + tooltipHeight > window.innerHeight) {
          top = window.innerHeight - tooltipHeight - 20;
        }

        tooltipElement.style.top = `${top}px`;
        tooltipElement.style.left = `${left}px`;
      }
    }
  }

  unhoveredFunction(){
    this.tooltip = false;
  }

  disableApplySequence() {
    const selectedDevicesList = Object.keys(this.selectedDevicesMap).filter((key) => this.selectedDevicesMap[key]);
    return !(this.originalAppliedList.length > 0 || selectedDevicesList.length > 0)
      || ((this.createdPointsCollection.length == 0 || this.invalidModels.length > 0) && selectedDevicesList.length > 0)
      || this.hasMismatchedSelectedDevices();
  }

  isSmartNodeDevice(device: any): boolean {
    const name = device?.deviceName?.toLowerCase?.() || '';
    return name.includes('smart node');
  }

  isConnectModuleDevice(device: any): boolean {
    const name = device?.deviceName?.toLowerCase?.() || '';
    return name.includes('connect module') || name.includes('connect node');
  }

  isDeviceTypeMismatch(device: any): boolean {
    const deviceType = this.deviceType || 'CONNECT_MODULE';
    if (deviceType === 'SMART_NODE') {
      return this.isConnectModuleDevice(device);
    }
    return this.isSmartNodeDevice(device);
  }

  getDeviceTypeLabel(): string {
    return this.deviceType === 'SMART_NODE' ? 'Smart Node' : 'Connect Module';
  }

  getOppositeDeviceTypeLabel(): string {
    return this.deviceType === 'SMART_NODE' ? 'Connect Module' : 'Smart Node';
  }

  hasMismatchedSelectedDevices(): boolean {
    return Object.keys(this.selectedDevicesMap).some((deviceRef) => {
      if (!this.selectedDevicesMap[deviceRef]) return false;
      const device = this.deviceRefMap[deviceRef];
      return device ? this.isDeviceTypeMismatch(device) : false;
    });
  }

  getDeviceTypeMismatchMessage(): string {
    const current = this.getDeviceTypeLabel();
    const previous = this.getOppositeDeviceTypeLabel();
    return `Some selected devices don’t match the current device type "${current}". Please uncheck all "${previous}" devices to apply the sequence.`;
  }

  //close the popup
  closePopup() {
    this.dialogRef.close({
      back: true,
      selectedDevicesMap: { ...this.selectedDevicesMap }
    });
  }
  
}
